import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import EmptyState from '../../../shared/components/EmptyState';
import LoadingState from '../../../shared/components/LoadingState';
import Modal from '../../../shared/components/Modal';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { isTenantAdminOrSystemAdmin } from '../../../shared/auth/access';
import {
  buildHeaders,
  clearSectorRatios,
  createSectorRatio,
  deleteSectorRatio,
  importSectorRatiosFromEvds,
  listSectorRatios,
  updateSectorRatio
} from '../../../services/sectorRatios';
import './SectorRatiosPage.css';

const STATUS_VALUES = {
  all: 'Tumu',
  active: 'Aktif',
  passive: 'Pasif'
};

const createEmptyForm = () => ({
  id: null,
  sectorId: '',
  periodId: '',
  ratioId: '',
  ratioValue: '',
  notes: '',
  isActive: true
});

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8010').replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Sektor Rasyolari',
    pageDescription: 'Sistem parametreleri icin sektorel benchmark degerleri yonetin.',
    importEvds: "Sektor Rasyolarini EVDS'den Al",
    importingEvds: "EVDS'den Aliniyor...",
    newRatio: 'Yeni rasyo',
    all: 'Tumu',
    active: 'Aktif',
    passive: 'Pasif',
    sector: 'Sektor',
    period: 'Donem',
    ratio: 'Rasyo',
    status: 'Durum',
    search: 'Arama',
    searchPlaceholder: 'Not, sektor, rasyo...',
    clearFilters: 'Filtreleri Temizle',
    value: 'Deger',
    notes: 'Not',
    actions: 'Islemler',
    loading: 'Yukleniyor...',
    noRecords: 'Henuz sektor rasyosu yok.',
    totalRecords: 'Toplam {{count}} kayit',
    previous: 'Onceki',
    next: 'Sonraki',
    edit: 'Duzenle',
    delete: 'Sil',
    close: 'Kapat',
    save: 'Kaydet',
    update: 'Guncelle',
    cancel: 'Iptal',
    saving: 'Kaydediliyor...',
    modalCreateAria: 'Yeni Rasyo Tanimi',
    modalEditAria: 'Rasyoyu Guncelle',
    modalCreateTitle: 'Yeni Rasyo Tanimi',
    modalEditTitle: 'Rasyoyu Guncelle',
    select: 'Seciniz',
    ratioValue: 'Rasyo Degeri *',
    notesPlaceholder: 'Aciklayici not...',
    isActive: 'Aktif',
    requiredFields: 'Tum zorunlu alanlari doldurun.',
    selectionsLoadError: 'Secimler yuklenemedi.',
    selectionsFetchError: 'Secimler alinamadi.',
    dataFetchError: 'Veri alinamadi.',
    operationFailed: 'Islem basarisiz oldu.',
    updateSuccess: 'Sektor rasyosu guncellendi.',
    createSuccess: 'Yeni sektor rasyosu eklendi.',
    deleteConfirm: 'Bu sektor rasyosunu silmek istediginizden emin misiniz?',
    deleteSuccess: 'Sektor rasyosu pasif hale getirildi.',
    deleteFailed: 'Silme basarisiz oldu.',
    importConfirm: "EVDS'den sektor rasyolari (2023-2024) yeniden alinacak. Devam edilsin mi?",
    importSuccessPrefix: 'EVDS import tamamlandi.',
    importFailed: 'EVDS import basarisiz oldu.',
    clearData: 'Verileri Temizle',
    clearDataConfirm: 'Secili donem (varsa) ve donemle iliskisi olmayan kayitlar temizlenecek. Devam edilsin mi?',
    clearDataSuccess: 'Sektor rasyo verileri temizlendi. Silinen kayit: {{count}}',
    clearDataFailed: 'Sektor rasyo verileri temizlenemedi.',
    importDatagroup: 'Datagroup',
    importSector: 'Sektor',
    importRatioDef: 'Rasyo tanimi',
    importRatioData: 'Rasyo veri'
  },
  'en-US': {
    pageTitle: 'Sector Ratios',
    pageDescription: 'Manage sector benchmark values for system parameters.',
    importEvds: 'Import Sector Ratios from EVDS',
    importingEvds: 'Importing from EVDS...',
    newRatio: 'New ratio',
    all: 'All',
    active: 'Active',
    passive: 'Passive',
    sector: 'Sector',
    period: 'Period',
    ratio: 'Ratio',
    status: 'Status',
    search: 'Search',
    searchPlaceholder: 'Note, sector, ratio...',
    clearFilters: 'Clear Filters',
    value: 'Value',
    notes: 'Note',
    actions: 'Actions',
    loading: 'Loading...',
    noRecords: 'No sector ratio yet.',
    totalRecords: 'Total {{count}} records',
    previous: 'Previous',
    next: 'Next',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    save: 'Save',
    update: 'Update',
    cancel: 'Cancel',
    saving: 'Saving...',
    modalCreateAria: 'New Ratio Definition',
    modalEditAria: 'Update Ratio',
    modalCreateTitle: 'New Ratio Definition',
    modalEditTitle: 'Update Ratio',
    select: 'Select',
    ratioValue: 'Ratio Value *',
    notesPlaceholder: 'Explanatory note...',
    isActive: 'Active',
    requiredFields: 'Please fill all required fields.',
    selectionsLoadError: 'Selections could not be loaded.',
    selectionsFetchError: 'Selections could not be fetched.',
    dataFetchError: 'Data could not be fetched.',
    operationFailed: 'Operation failed.',
    updateSuccess: 'Sector ratio updated.',
    createSuccess: 'New sector ratio added.',
    deleteConfirm: 'Are you sure you want to delete this sector ratio?',
    deleteSuccess: 'Sector ratio has been deactivated.',
    deleteFailed: 'Delete failed.',
    importConfirm: 'Sector ratios (2023-2024) will be re-imported from EVDS. Continue?',
    importSuccessPrefix: 'EVDS import completed.',
    importFailed: 'EVDS import failed.',
    clearData: 'Clear Data',
    clearDataConfirm: 'Selected period records (if any) and orphan period records will be cleared. Continue?',
    clearDataSuccess: 'Sector ratio data cleared. Deleted records: {{count}}',
    clearDataFailed: 'Sector ratio data could not be cleared.',
    importDatagroup: 'Datagroup',
    importSector: 'Sector',
    importRatioDef: 'Ratio definition',
    importRatioData: 'Ratio data'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const fetchSelections = async (endpoints, t) => {
  const headers = { ...buildHeaders() };
  const responses = await Promise.all(endpoints.map((endpoint) => fetch(`${API_BASE_URL}${endpoint}`, { headers })));
  const succeeded = responses.every((response) => response.ok);
  if (!succeeded) throw new Error(t('selectionsLoadError'));
  return Promise.all(responses.map((response) => response.json()));
};

function SectorRatiosPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, offset: 0, limit: 50 });
  const [filters, setFilters] = useState({ sectorCode: '', periodCode: '', ratioCode: '', status: STATUS_VALUES.active, search: '' });
  const [formState, setFormState] = useState(createEmptyForm());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listError, setListError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [sectors, setSectors] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [ratios, setRatios] = useState([]);
  const canClearData = useMemo(() => isTenantAdminOrSystemAdmin(), []);

  const isEditMode = Boolean(formState.id);

  const statusLabel = useCallback((value) => {
    if (value === STATUS_VALUES.active) return t('active');
    if (value === STATUS_VALUES.passive) return t('passive');
    return t('all');
  }, [t]);

  const loadSelections = useCallback(async () => {
    const [sectorData, periodData, ratioData] = await fetchSelections(['/api/sectors', '/api/periods', '/api/ratios/definitions?isActive=true'], t);
    setSectors(sectorData);
    setPeriods(periodData);
    setRatios(ratioData);
  }, [t]);

  const loadSectorRatios = useCallback(async () => {
    setLoading(true);
    setListError('');
    try {
      const params = {
        sectorCode: filters.sectorCode,
        periodCode: filters.periodCode,
        ratioCode: filters.ratioCode,
        status: filters.status === STATUS_VALUES.all ? undefined : filters.status,
        search: filters.search,
        offset: meta.offset,
        limit: meta.limit
      };
      const response = await listSectorRatios(params);
      setRows(response.items);
      setMeta((prev) => ({ ...prev, total: response.total }));
    } catch (error) {
      setListError(error instanceof Error ? error.message : t('dataFetchError'));
    } finally {
      setLoading(false);
    }
  }, [filters, meta.limit, meta.offset, t]);

  useEffect(() => {
    loadSectorRatios();
  }, [loadSectorRatios]);

  useEffect(() => {
    loadSelections().catch((error) => {
      setListError(error instanceof Error ? error.message : t('selectionsFetchError'));
    });
  }, [loadSelections, t]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setMeta((prev) => ({ ...prev, offset: 0 }));
  };

  const handlePage = (direction) => {
    setMeta((prev) => {
      const nextOffset = direction === 'prev' ? Math.max(0, prev.offset - prev.limit) : prev.offset + prev.limit;
      return { ...prev, offset: nextOffset };
    });
  };

  const resetForm = useCallback(() => {
    setFormState(createEmptyForm());
    setFormError('');
  }, []);

  const openCreateModal = () => {
    resetForm();
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    resetForm();
  }, [resetForm]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError('');
    setSuccess('');

    if (!formState.sectorId || !formState.periodId || !formState.ratioId || !formState.ratioValue) {
      setFormError(t('requiredFields'));
      setSaving(false);
      return;
    }

    const payload = {
      sectorId: Number(formState.sectorId),
      periodId: Number(formState.periodId),
      ratioId: Number(formState.ratioId),
      ratioValue: Number(formState.ratioValue),
      notes: formState.notes || null,
      isActive: Boolean(formState.isActive)
    };

    try {
      if (isEditMode) {
        await updateSectorRatio(formState.id, payload);
        setSuccess(t('updateSuccess'));
      } else {
        await createSectorRatio(payload);
        setSuccess(t('createSuccess'));
      }
      closeModal();
      setMeta((prev) => ({ ...prev, offset: 0 }));
      await loadSectorRatios();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setFormState({
      id: row.id,
      sectorId: row.sectorId?.toString() || '',
      periodId: row.periodId?.toString() || '',
      ratioId: row.ratioId?.toString() || '',
      ratioValue: row.ratioValue ?? '',
      notes: row.notes || '',
      isActive: row.isActive ?? true
    });
    setFormError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t('deleteConfirm'))) return;

    setSaving(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      await deleteSectorRatio(row.id);
      setSuccess(t('deleteSuccess'));
      if (formState.id === row.id) closeModal();
      await loadSectorRatios();
    } catch (error) {
      setListError(error instanceof Error ? error.message : t('deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleImportFromEvds = async () => {
    if (!window.confirm(t('importConfirm'))) return;

    setImporting(true);
    setListError('');
    setFormError('');
    setSuccess('');

    try {
      const result = await importSectorRatiosFromEvds();
      const info = [
        `${t('importDatagroup')}: ${result.datagroupsProcessed}`,
        `${t('importSector')}: +${result.sectorsCreated} / ~${result.sectorsUpdated}`,
        `${t('importRatioDef')}: +${result.ratioDefinitionsCreated} / ~${result.ratioDefinitionsUpdated}`,
        `${t('importRatioData')}: +${result.sectorRatiosInserted} / ~${result.sectorRatiosUpdated}`
      ].join(' | ');
      setSuccess(`${t('importSuccessPrefix')} ${info}`);
      setMeta((prev) => ({ ...prev, offset: 0 }));
      await Promise.all([loadSelections(), loadSectorRatios()]);
    } catch (error) {
      setListError(error instanceof Error ? error.message : t('importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const handleClearData = async () => {
    const selectedPeriod = periods.find((period) => period.code === filters.periodCode);
    if (!window.confirm(t('clearDataConfirm'))) return;

    const selectedSector = sectors.find((sector) => sector.code === filters.sectorCode);
    const selectedRatio = ratios.find((ratio) => ratio.ratioCode === filters.ratioCode);

    setClearing(true);
    setListError('');
    setFormError('');
    setSuccess('');
    try {
      const result = await clearSectorRatios({
        periodId: selectedPeriod?.id,
        sectorId: selectedSector?.id,
        ratioId: selectedRatio?.id,
        includeOrphanPeriods: true
      });
      setSuccess(t('clearDataSuccess').replace('{{count}}', String(result.deletedCount || 0)));
      setMeta((prev) => ({ ...prev, offset: 0 }));
      await loadSectorRatios();
    } catch (error) {
      setListError(error instanceof Error ? error.message : t('clearDataFailed'));
    } finally {
      setClearing(false);
    }
  };

  const tableRows = useMemo(() => rows || [], [rows]);

  return (
    <div className="card sector-ratios-card">
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <div className="sector-ratios-actions">
            <button type="button" className="btn-secondary" onClick={handleImportFromEvds} disabled={importing || loading || saving}>
              {importing ? t('importingEvds') : t('importEvds')}
            </button>
            {canClearData ? (
              <button
                type="button"
                className="btn-danger"
                onClick={handleClearData}
                disabled={importing || loading || saving || clearing}
              >
                {clearing ? `${t('clearData')}...` : t('clearData')}
              </button>
            ) : null}
            <button type="button" className="btn-secondary" onClick={openCreateModal} disabled={importing}>
              {t('newRatio')}
            </button>
          </div>
        }
      />
      {success && <div className="alert alert-success">{success}</div>}

      <div className="sector-ratios-grid">
        <section className="sector-ratios-table-panel">
          <div className="filter-row">
            <label>
              {t('sector')}
              <select value={filters.sectorCode} onChange={(event) => handleFilterChange('sectorCode', event.target.value)}>
                <option value="">{t('all')}</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.code}>{sector.code} - {sector.name}</option>
                ))}
              </select>
            </label>
            <label>
              {t('period')}
              <select value={filters.periodCode} onChange={(event) => handleFilterChange('periodCode', event.target.value)}>
                <option value="">{t('all')}</option>
                {periods.map((period) => (
                  <option key={period.id} value={period.code}>{period.code}</option>
                ))}
              </select>
            </label>
            <label>
              {t('ratio')}
              <select value={filters.ratioCode} onChange={(event) => handleFilterChange('ratioCode', event.target.value)}>
                <option value="">{t('all')}</option>
                {ratios.map((ratio) => (
                  <option key={ratio.id} value={ratio.ratioCode}>{ratio.ratioCode} - {ratio.name}</option>
                ))}
              </select>
            </label>
            <label>
              {t('status')}
              <select value={filters.status} onChange={(event) => handleFilterChange('status', event.target.value)}>
                {[STATUS_VALUES.all, STATUS_VALUES.active, STATUS_VALUES.passive].map((option) => (
                  <option key={option} value={option}>{statusLabel(option)}</option>
                ))}
              </select>
            </label>
            <label className="search-field">
              {t('search')}
              <input type="text" value={filters.search} placeholder={t('searchPlaceholder')} onChange={(event) => handleFilterChange('search', event.target.value)} />
            </label>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setFilters({ sectorCode: '', periodCode: '', ratioCode: '', status: STATUS_VALUES.active, search: '' });
                setMeta((prev) => ({ ...prev, offset: 0 }));
              }}
            >
              {t('clearFilters')}
            </button>
          </div>

          <div className="table-wrapper">
            <table className="sector-ratios-table">
              <thead>
                <tr>
                  <th>{t('sector')}</th>
                  <th>{t('period')}</th>
                  <th>{t('ratio')}</th>
                  <th>{t('value')}</th>
                  <th>{t('notes')}</th>
                  <th>{t('status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="table-placeholder">
                      <LoadingState message={t('loading')} />
                    </td>
                  </tr>
                ) : listError ? (
                  <tr><td colSpan={7} className="table-placeholder">{listError}</td></tr>
                ) : tableRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table-placeholder">
                      <EmptyState title={t('noRecords')} />
                    </td>
                  </tr>
                ) : (
                  tableRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.sectorCode}</strong>
                        <div className="muted">{row.sectorName}</div>
                      </td>
                      <td>
                        <strong>{row.periodCode}</strong>
                        <div className="muted">{row.periodDescription}</div>
                      </td>
                      <td>
                        <strong>{row.ratioCode}</strong>
                        <div className="muted">{row.ratioName}</div>
                      </td>
                      <td>{row.ratioValue}</td>
                      <td className="notes-cell">{row.notes || '-'}</td>
                      <td>
                        <span className={`badge ${row.isActive ? 'badge-active' : ''}`}>{row.isActive ? t('active') : t('passive')}</span>
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-edit"
                          onClick={() => handleEdit(row)}
                          aria-label={t('edit')}
                          title={t('edit')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M3 17.25V21h3.75L18.81 8.94l-3.75-3.75L3 17.25Zm17.71-10.04a1.003 1.003 0 0 0 0-1.42L18.2 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 2.01-1.66Z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="action-icon-btn action-icon-btn-delete"
                          onClick={() => handleDelete(row)}
                          aria-label={t('delete')}
                          title={t('delete')}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M6 7h12l-1 14H7L6 7Zm3-3h6l1 2h4v2H4V6h4l1-2Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {tableRows.length > 0 && (
            <div className="pagination-row">
              <span>{t('totalRecords').replace('{{count}}', String(meta.total))}</span>
              <div className="pagination-actions">
                <button type="button" className="btn-secondary" disabled={meta.offset === 0} onClick={() => handlePage('prev')}>
                  {t('previous')}
                </button>
                <button type="button" className="btn-secondary" disabled={meta.offset + meta.limit >= meta.total} onClick={() => handlePage('next')}>
                  {t('next')}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={isEditMode ? t('modalEditTitle') : t('modalCreateTitle')}
        width="560px"
      >
        {formError && <div className="alert alert-error">{formError}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label>{t('sector')} *</label>
            <select value={formState.sectorId} onChange={(event) => setFormState((current) => ({ ...current, sectorId: event.target.value }))} required>
              <option value="">{t('select')}</option>
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.id}>{sector.code} - {sector.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>{t('period')} *</label>
            <select value={formState.periodId} onChange={(event) => setFormState((current) => ({ ...current, periodId: event.target.value }))} required>
              <option value="">{t('select')}</option>
              {periods.map((period) => (
                <option key={period.id} value={period.id}>{period.code}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>{t('ratio')} *</label>
            <select value={formState.ratioId} onChange={(event) => setFormState((current) => ({ ...current, ratioId: event.target.value }))} required>
              <option value="">{t('select')}</option>
              {ratios.map((ratio) => (
                <option key={ratio.id} value={ratio.id}>{ratio.ratioCode} - {ratio.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label>{t('ratioValue')}</label>
            <input
              type="number"
              step="0.0001"
              value={formState.ratioValue}
              onChange={(event) => setFormState((current) => ({ ...current, ratioValue: event.target.value }))}
              placeholder="0.00"
              required
            />
          </div>
          <div className="form-field">
            <label>{t('notes')}</label>
            <textarea rows={3} value={formState.notes} onChange={(event) => setFormState((current) => ({ ...current, notes: event.target.value }))} placeholder={t('notesPlaceholder')} />
          </div>
          <div className="form-field checkbox-field">
            <label>
              <input type="checkbox" checked={Boolean(formState.isActive)} onChange={(event) => setFormState((current) => ({ ...current, isActive: event.target.checked }))} />
              {t('isActive')}
            </label>
          </div>
          <div className="form-footer">
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? t('saving') : isEditMode ? t('update') : t('save')}</button>
            <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>{t('cancel')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default SectorRatiosPage;
